import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bug, Building2, ChevronsUpDown, Menu as MenuIcon, MessagesSquare, Mic, Shield, Sparkles, Terminal } from 'lucide-react'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { AccountMenu, type ProfileReturn } from '@/components/account/AccountMenu'
import { logInLink } from '@/components/onboarding/authContinuation'
import { mayLeave, useLeaveGuard, type BeforeLeave } from '@/components/siteLeave'
import {
  Button,
  ButtonLink,
  Drawer,
  IconButton,
  Menu,
  MenuLinkItem,
  MenuRadioGroup,
  NavigationMenu,
  Select,
  Tooltip,
  type NavigationGroup,
  type NavigationLink,
  type NavigationMenuItem,
} from '@/components/play'
import { useAuth } from '@/state/AuthContext'
import { isViewer, withoutOrganization } from '@/components/auth/isViewer'
import { isDirectorEnabled } from '@/lib/features'
import { canAccessAdmin } from '@/lib/adminCapabilities'
import { cn } from '@/lib/utils'
import { api } from '@/util/api'
import type { OrgResource } from '@/types/admin'
import './site-header.css'

export type { BeforeLeave, SiteLeave } from '@/components/siteLeave'

/** How to run StreamBridge from an agent or from ChatGPT. `guides` lead the group: hosts find the watch-along guide
 *  here. */
function docsGroup(pathname: string, guides: readonly NavigationLink[] = []): NavigationGroup {
  return {
    label: 'Docs',
    links: [
      ...guides,
      { to: '/docs/mcp', label: 'CLI, MCP & skills', description: 'Run events from your terminal or AI agent.', icon: <Terminal />, current: pathname === '/docs/mcp' },
      { to: '/docs/chatgpt', label: 'ChatGPT plugin', description: 'Create events and invite cameras in ChatGPT.', icon: <MessagesSquare />, current: pathname === '/docs/chatgpt' },
    ],
  }
}

/** An event's own logo for the header's top left (SiteHeader's `eventLogo`): the picture, the event's name (the link's
 *  accessible name) and where it goes, the event's page. */
export type SiteEventLogo = { src: string; name: string; to: string }

type SiteHeaderProps = {
  /** `inverse` for a header over dark photography, such as the home page hero. */
  tone?: 'default' | 'inverse'
  /** The row spans the viewport instead of stopping at the content width, so the logo lines up with a full-bleed
   *  canvas such as the watch screen. The side padding stays. */
  fullWidth?: boolean
  /** Asked before the header takes the person off the page; return false to hold it. See SiteHeader. */
  beforeLeave?: BeforeLeave
  /** Opens the page's own sign-in, such as the watch screen's sheet: Log in becomes a button that calls it, in the bar
   *  and in the drawer (which it closes), instead of a link to the sign-in page. */
  onLogIn?: () => void
  /** The account menu's Edit profile comes back here (AccountMenu's `profileReturn`). */
  profileReturn?: ProfileReturn
  /** The header floats over full-bleed media at the top of the page (the watch screen's video on a phone): the
   *  supplied compact play mark in place of the lockup, with its name as a tooltip, then the account menu and the
   *  drawer's trigger in ink, as the dark photography's controls. The navigation, Log in and a superadmin's
   *  organization switch stay in the drawer at every width. Its links, drawer and account menu are the header's own.
   *  The page places it over its media (`play-site-header--overlay`). */
  overlay?: boolean
  /** The event's logo in place of StreamBridge's, top left, going to the event's page and named for the event (the
   *  watch screen of an event that asks for it). It keeps the lockup's box, contained in it, so the row's layout stays
   *  whatever the logo's shape; over media (`overlay`) it sits on a white tile at the mark's height, named in a tooltip.
   *  The leave guard asks before it as before the logo. A logo that can't be shown gives way to StreamBridge's. Without
   *  it, StreamBridge's logo as ever. */
  eventLogo?: SiteEventLogo
  className?: string
}

/** The top menu for the site and the signed-in app: the logo, the main navigation and the account menu or Log in.
 *  Visitors and viewers get The experience, Host a watch-along, News and Docs. Hosts get Events, Feedback, by role and
 *  feature Admin and AI Producer (one Tools group when both apply, so the bar holds five items at most), News, and Docs,
 *  led by the watch-along guide. While the account loads, News and Docs, which are the same for everyone. Below 960px the navigation moves into a drawer; the
 *  account menu stays. Event workspaces and the account page keep their own quiet header. Log in comes back to the
 *  page it was opened from, except from the home page and the sign-in pages, where it lands on Events.
 *
 *  `beforeLeave` lets a page hold leaving, such as the watch screen while the host is live. The header asks it
 *  (`SiteLeave`) before following one of its links in this tab: the logo, the bar's and the drawer's links (groups
 *  such as Docs included), Log in, and the account menu's Edit profile and other links (`navigate`, with `to`); before
 *  Sign out (`sign_out`); and before a superadmin's organization switch, which reloads the app (`switch_organization`).
 *  Return false and nothing leaves, though the menu or drawer that was used closes; the page calls `leave.proceed()`
 *  later, which navigates to `to`, signs out or switches. Clicks that open a new tab or window (a modifier key or the
 *  middle button) and links outside the router pass through without asking. */
export function SiteHeader({ tone = 'default', fullWidth = false, beforeLeave, onLogIn, profileReturn, overlay = false, eventLogo, className }: SiteHeaderProps) {
  const { isAuthenticated, authReady, user, organization, switchOrganization } = useAuth()
  const follow = useLeaveGuard(beforeLeave)
  const location = useLocation()
  const pathname = location.pathname.replace(/(.)\/$/, '$1')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [organizations, setOrganizations] = useState<OrgResource[]>([])
  // An event logo that can't be shown (a file the browser won't draw, such as an SVG the API serves as a download)
  // gives way to StreamBridge's, so the header never shows a broken picture; another logo is tried afresh.
  const [failedLogo, setFailedLogo] = useState<string | null>(null)
  const shownLogo = eventLogo && eventLogo.src !== failedLogo ? eventLogo : undefined
  const role = user?.attributes?.role
  const isSuperadmin = role === 'superadmin'
  const inverse = tone === 'inverse'

  useEffect(() => {
    if (!isSuperadmin || !isAuthenticated) return
    api.get<{ data: OrgResource[] }>('/organizations').then((res) => setOrganizations(res.data)).catch(() => {})
  }, [isSuperadmin, isAuthenticated])

  // Host pages need an organisation (or a superadmin), or an event someone manages for another organisation
  // (docs/features/event-managers.md). A viewer, signed in with neither, gets the public navigation and a logo that goes
  // home; while the account loads nobody gets either, so host links never flash for a viewer. Admin and Feedback stay
  // with organisations (the API takes feedback from an organisation's members).
  const accountLoading = isAuthenticated && !authReady
  const host = isAuthenticated && authReady && !isViewer(organization, user)
  const within = (href: string) => pathname === href || pathname.startsWith(`${href}/`)
  const watchAlong: NavigationLink = {
    to: '/host', label: 'Host a watch-along', description: 'Go live on your event. You call the cameras.', icon: <Mic />, current: within('/host'),
  }
  // News (/insights): the articles, the same for everyone.
  const news: NavigationLink = { to: '/insights', label: 'News', current: within('/insights') }
  // Five items at most, Upcoming events included once it's back: Admin and AI Producer share a Tools group when both
  // apply.
  const tools: NavigationLink[] = [
    ...(canAccessAdmin(role) && !withoutOrganization(organization, user)
      ? [{ to: '/admin', label: 'Admin', description: 'Manage users, teams and settings.', icon: <Shield />, current: within('/admin') }]
      : []),
    ...(isDirectorEnabled(organization)
      ? [{ to: '/app/ai-producer', label: 'AI Producer', description: 'Start an event from a prompt.', icon: <Sparkles />, current: within('/app/ai-producer') }]
      : []),
  ]
  // Upcoming events (/explore) is parked: see docs/features/explore.md. To restore it, add
  // `{ to: '/explore', label: 'Upcoming events', current: within('/explore') }` back here: after Events for hosts, and
  // first for everyone else, the loading state included (public events are the same for everyone).
  const items: NavigationMenuItem[] = host
    ? [
        { to: '/app', label: 'Events', current: pathname === '/app' || pathname === '/dashboard' },
        ...(withoutOrganization(organization, user) ? [] : [{ to: '/feedback', label: 'Feedback', current: within('/feedback') }]),
        ...(tools.length > 1 ? [{ label: 'Tools', links: tools }] : tools),
        news,
        docsGroup(pathname, [watchAlong]),
      ]
    : accountLoading
      ? [news, docsGroup(pathname)]
      : [{ to: '/#experience', label: 'The experience' }, watchAlong, news, docsGroup(pathname)]
  const closeDrawer = () => setDrawerOpen(false)
  const switchTo = (organizationId: string) => {
    const leave = () => { switchOrganization(organizationId) }
    if (mayLeave(beforeLeave, { kind: 'switch_organization', organizationId }, leave)) leave()
  }
  const home = host ? '/app' : '/'
  const loginUrl = logInLink(location)
  const switchableOrganization = isAuthenticated && isSuperadmin && organizations.length > 1 ? organization : null
  const organizationItems = organizations.map((org) => ({ value: org.id, label: org.attributes.name }))
  const ghost = inverse ? 'inverse' : 'ghost'
  const homeLabel = shownLogo ? shownLogo.name : host ? 'StreamBridge events' : 'StreamBridge home'
  const homeLink = shownLogo ? (
    <Link
      to={shownLogo.to}
      onClick={(event) => follow(event, shownLogo.to)}
      className="play-site-header__home play-site-header__home--event"
      aria-label={homeLabel}
    >
      <img className="play-site-header__event-logo" src={shownLogo.src} alt="" onError={() => setFailedLogo(shownLogo.src)} />
    </Link>
  ) : (
    <Link to={home} onClick={(event) => follow(event, home)} className="play-site-header__home" aria-label={homeLabel}>
      {overlay ? <img className="play-site-header__mark" src="/brand/play/mark.svg" width="48" height="48" alt="" /> : <PlayLogo />}
    </Link>
  )

  return (
    <header className={cn('play-site-header', fullWidth && 'play-site-header--full-width', overlay && 'play-site-header--overlay', className)}>
      {/* Over media the mark (or the event's logo) stands alone, so its name shows as a tooltip, as an icon's does. */}
      {overlay ? <Tooltip content={homeLabel}>{homeLink}</Tooltip> : homeLink}

      {!overlay && (
        <NavigationMenu label="Main" items={items} tone={tone} onNavigate={(event, link) => follow(event, link.to)} className="play-site-header__nav" />
      )}

      <div className="play-site-header__account">
        {switchableOrganization && !overlay && (
          <Menu
            align="end"
            trigger={
              <Button variant={ghost} className="play-site-header__organization-switch">
                <Building2 aria-hidden="true" />
                <span className="play-site-header__organization">{switchableOrganization.attributes.name}</span>
                <ChevronsUpDown aria-hidden="true" />
              </Button>
            }
          >
            <MenuRadioGroup
              label="Switch organization"
              items={organizationItems}
              value={switchableOrganization.id}
              onValueChange={switchTo}
            />
          </Menu>
        )}
        {isAuthenticated ? (
          <AccountMenu beforeLeave={beforeLeave} profileReturn={profileReturn}>
            {import.meta.env.DEV && (
              <MenuLinkItem href="http://localhost:3000/letter_opener" target="_blank" rel="noreferrer" icon={<Bug />}>Debug: Emails</MenuLinkItem>
            )}
          </AccountMenu>
        ) : overlay ? null : onLogIn ? (
          <Button variant={ghost} onClick={() => onLogIn()} className="play-site-header__login">Log in</Button>
        ) : (
          <ButtonLink to={loginUrl} onClick={(event) => follow(event, loginUrl)} variant={ghost} className="play-site-header__login">Log in</ButtonLink>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        size="narrow"
        title="Navigation"
        hideTitle
        trigger={<IconButton label="Open navigation" variant={overlay ? 'inverse' : ghost} className="play-site-header__drawer-trigger"><MenuIcon /></IconButton>}
      >
        <NavigationMenu
          label="Main"
          orientation="vertical"
          items={items}
          onNavigate={(event, link) => {
            closeDrawer()
            follow(event, link.to)
          }}
        />
        {switchableOrganization && (
          <div className="play-site-header__drawer-account">
            <Select
              label="Organization"
              items={organizationItems}
              value={switchableOrganization.id}
              onValueChange={(orgId) => {
                if (!orgId) return
                closeDrawer()
                switchTo(orgId)
              }}
            />
          </div>
        )}
        {!isAuthenticated && (
          <div className="play-site-header__drawer-account">
            {onLogIn ? (
              <Button
                onClick={() => {
                  closeDrawer()
                  onLogIn()
                }}
              >
                Log in
              </Button>
            ) : (
              <ButtonLink
                to={loginUrl}
                onClick={(event) => {
                  closeDrawer()
                  follow(event, loginUrl)
                }}
              >
                Log in
              </ButtonLink>
            )}
          </div>
        )}
      </Drawer>
    </header>
  )
}
